import React, { useState, useEffect, useMemo } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Check, UserX, Users, GripVertical, X, Ticket } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import type { Performer, JamNight } from "@shared/schema";
import ParticipantActionsDropdown from "@/components/participant-actions-dropdown";

interface GenericEventAttendedTableProps {
  performers: Performer[];
  jamNightId?: number;
  jamNight?: JamNight | undefined;
  disabled?: boolean;
}

interface AttendedRowProps {
  performer: Performer;
  onMarkNotAttended: (performer: Performer) => void;
  onRemove: (performerId: number) => void;
  isLoading: boolean;
  disabled?: boolean;
  jamNight?: JamNight | undefined;
}

const AttendedMobileCard = React.memo(function AttendedMobileCard({ 
  performer, 
  onMarkNotAttended, 
  onRemove, 
  isLoading, 
  disabled = false,
  jamNight
}: AttendedRowProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: performer.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition: isDragging ? 'none' : transition,
    opacity: isDragging ? 0.5 : 1,
    position: 'relative' as const,
    zIndex: isDragging ? 50 : 'auto',
    willChange: isDragging ? 'transform' : 'auto',
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`border-b rounded-lg mb-2 ${
        isDragging 
          ? "bg-white border-slate-300 shadow-lg z-50" 
          : "bg-white/20 border-white/20"
      } last:border-b-0 p-4`}
    >
      <div
        {...(disabled ? {} : attributes)}
        {...(disabled ? {} : listeners)}
        className={`absolute left-0 top-0 h-full w-1/4 ${disabled ? 'cursor-not-allowed' : 'cursor-grab'} touch-none`}
        style={{ touchAction: "none" }}
      />
      <div className="flex justify-between items-start mb-3">
        <div className="flex items-start flex-1">
          <div
            className="mr-3 text-slate-400 hover:text-slate-600 mt-1"
            style={{ touchAction: 'none' }}
          >
            <GripVertical className="h-4 w-4" />
          </div>
          <div className="flex-1" style={{ touchAction: 'auto' }}>
            <div className="text-lg font-medium text-slate-900 mb-1">
              {performer.name}
            </div>
            <div className="flex flex-wrap gap-2 mb-0 -ml-6">
              <Badge className="bg-green-100 text-green-800">
                <Check className="h-3 w-3 mr-1" />
                Attended
              </Badge>
              {jamNight?.raffleEnabled && (performer.raffleEntrances > 0 || performer.raffleWins > 0) && (
                <Badge className="bg-blue-100/60 backdrop-blur-sm text-blue-800 border border-blue-200/50">
                  <Ticket className="h-3 w-3 mr-1" />
                  {Math.max(0, performer.raffleEntrances - performer.raffleWins)} remaining entries · {performer.raffleWins} wins
                </Badge>
              )}
            </div>
          </div>
        </div>
      </div>
      
      <div className="flex gap-2" style={{ touchAction: 'auto' }}>
        <Button
          variant="outline"
          size="sm"
          onClick={() => onMarkNotAttended(performer)}
          disabled={disabled || isLoading}
          className="bg-yellow-100/40 backdrop-blur-sm border-yellow-200/50 text-yellow-700 hover:bg-yellow-100/60 hover:border-yellow-300/60 flex-1 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <UserX className="h-3 w-3 mr-1" />
          Mark Not Attended
        </Button>
        
        <ParticipantActionsDropdown
          performer={performer}
          onRemove={onRemove}
          disabled={disabled}
          isLoading={isLoading}
          jamNight={jamNight}
          jamNightId={jamNight?.id}
          isDragging={isDragging}
        />
      </div>
      
      {/* Priority Banner */}
      {performer.priority && (
        <div className="bg-orange-100 border-l-4 border-orange-500 p-2 mt-3 rounded-r">
          <div className="flex items-center">
            <div className="w-2 h-2 bg-orange-500 rounded-full mr-2 animate-pulse"></div>
            <span className="text-orange-800 font-medium text-sm">Priority Participant</span>
          </div>
        </div>
      )}
    </div>
  );
});

const AttendedRow = React.memo(function AttendedRow({ 
  performer, 
  onMarkNotAttended, 
  onRemove, 
  isLoading, 
  disabled = false,
  jamNight
}: AttendedRowProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: performer.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition: isDragging ? 'none' : transition,
    opacity: isDragging ? 0.5 : 1,
    willChange: isDragging ? 'transform' : 'auto',
  };

  return (
    <tr
      ref={setNodeRef}
      style={style}
      className={`${isDragging 
        ? "bg-white border border-slate-300 shadow-lg z-50" 
        : "hover:bg-white/20"
      }`}
    >
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="flex items-center">
          <div
            {...(disabled ? {} : attributes)}
            {...(disabled ? {} : listeners)}
            className={`${disabled ? 'cursor-not-allowed' : 'cursor-grab active:cursor-grabbing'} mr-3 text-slate-400 hover:text-slate-600`}
          >
            <GripVertical className="h-4 w-4" />
          </div>
          <div className="text-lg font-medium text-slate-900/90 drop-shadow-sm">
            {performer.name}
          </div>
        </div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        {performer.priority ? (
          <Badge className={`${isDragging 
            ? "bg-orange-100 text-orange-800 border border-orange-200 shadow-sm" 
            : "bg-orange-100/60 backdrop-blur-sm text-orange-800/90 border border-orange-200/50"
          }`} style={isDragging ? {} : {
            boxShadow: '0 2px 8px rgba(251,146,60,0.1), inset 0 1px 0 rgba(255,255,255,0.3)',
          }}>
            <div className="w-2 h-2 bg-orange-500 rounded-full mr-1 animate-pulse drop-shadow-sm"></div>
            Priority
          </Badge>
        ) : (
          <span className="text-slate-400/80 text-sm drop-shadow-sm">-</span>
        )}
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="flex items-center text-green-600/90">
          <Check className="h-4 w-4 mr-1 drop-shadow-sm" />
          Yes
        </div>
      </td>
      {jamNight?.raffleEnabled && (
        <td className="px-6 py-4 whitespace-nowrap">
          {(performer.raffleEntrances > 0 || performer.raffleWins > 0) ? (
            <div className="flex items-center gap-2">
              <Badge className={`${isDragging 
                ? "bg-blue-100 text-blue-800 border border-blue-200 shadow-sm" 
                : "bg-blue-100/60 backdrop-blur-sm text-blue-800/90 border border-blue-200/50"
              }`} style={isDragging ? {} : {
                boxShadow: '0 2px 8px rgba(59,130,246,0.1), inset 0 1px 0 rgba(255,255,255,0.3)',
              }}>
                <Ticket className="h-3 w-3 mr-1 drop-shadow-sm" />
                {Math.max(0, performer.raffleEntrances - performer.raffleWins)}
              </Badge>
              {performer.raffleWins > 0 && (
                <Badge className={`${isDragging 
                  ? "bg-yellow-100 text-yellow-800 border border-yellow-200 shadow-sm" 
                  : "bg-yellow-100/60 backdrop-blur-sm text-yellow-800/90 border border-yellow-200/50"
                }`} style={isDragging ? {} : {
                  boxShadow: '0 2px 8px rgba(251,191,36,0.1), inset 0 1px 0 rgba(255,255,255,0.3)',
                }}>
                  🏆 {performer.raffleWins}
                </Badge>
              )}
            </div>
          ) : (
            <span className="text-slate-400/80 text-sm drop-shadow-sm">-</span>
          )}
        </td>
      )}
      <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500 space-x-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => onMarkNotAttended(performer)}
          disabled={disabled || isLoading}
          className="bg-yellow-100/40 backdrop-blur-sm border-yellow-200/50 text-yellow-700 hover:bg-yellow-100/60 hover:border-yellow-300/60 shadow-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
          style={{
            boxShadow: '0 4px 16px rgba(251,191,36,0.1), inset 0 1px 0 rgba(255,255,255,0.3)',
          }}
        >
          <UserX className="h-3 w-3 mr-1" />
          Mark Not Attended
        </Button>
        
        <ParticipantActionsDropdown
          performer={performer}
          onRemove={onRemove}
          disabled={disabled}
          isLoading={isLoading}
          jamNight={jamNight}
          jamNightId={jamNight?.id}
          isDragging={isDragging}
        />
      </td>
    </tr>
  );
});

export default function GenericEventAttendedTable({
  performers,
  jamNightId,
  jamNight,
  disabled = false,
}: GenericEventAttendedTableProps) {
  const [searchTerm, setSearchTerm] = useState("");
  const [priorityFilter, setPriorityFilter] = useState<"all" | "priority" | "regular">("all");
  const [sortedPerformers, setSortedPerformers] = useState<Performer[]>([]);
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 3,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 50,
        tolerance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Filter out removed performers - MEMOIZED to prevent infinite loops
  const availablePerformers = useMemo(() => 
    performers.filter((performer) => !performer.removed),
    [performers]
  );

  // Initialize sorted performers when performers change
  useEffect(() => {
    // Sort performers by sortOrder, treating null/undefined as needing initialization
    const sorted = [...availablePerformers].sort((a, b) => {
      const soA = a.sortOrder ?? Infinity;
      const soB = b.sortOrder ?? Infinity;
      if (soA === soB) {
        // If sortOrder is same, sort by id for stable sort
        return (a.id ?? 0) - (b.id ?? 0);
      }
      return soA - soB;
    });
    setSortedPerformers(sorted);
  }, [availablePerformers]);

  // Handle drag end
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = sortedPerformers.findIndex(p => p.id === active.id);
      const newIndex = sortedPerformers.findIndex(p => p.id === over.id);
      
      const newOrder = arrayMove(sortedPerformers, oldIndex, newIndex);
      setSortedPerformers(newOrder);

      // Simple approach: just renumber everything based on new positions
      newOrder.forEach((performer, index) => {
        const newSortOrder = (index + 1) * 1000;
        if (performer.sortOrder !== newSortOrder) {
          updatePerformerMutation.mutate({
            id: performer.id,
            updates: { sortOrder: newSortOrder },
          });
        }
      });
    }
  };

  const updatePerformerMutation = useMutation({
    mutationFn: async ({
      id,
      updates,
    }: {
      id: number;
      updates: Partial<Performer>;
    }) => {
      const response = await apiRequest(
        "PATCH",
        `/api/performers/${id}`,
        updates,
      );
      return response.json();
    },
    onSuccess: () => {
      if (jamNightId) {
        queryClient.invalidateQueries({
          queryKey: [`/api/performers?jamNightId=${jamNightId}`],
        });
      } else {
        queryClient.invalidateQueries({ queryKey: ["/api/performers"] });
      }
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to update participant",
        variant: "destructive",
      });
    },
  });

  const handleMarkNotAttended = (performer: Performer) => {
    updatePerformerMutation.mutate({
      id: performer.id,
      updates: {
        attended: false,
      },
    });
  };

  const handleRemovePerformer = (performerId: number) => {
    updatePerformerMutation.mutate({
      id: performerId,
      updates: {
        removed: true,
        attended: false,
      },
    });
  };

  // Filter performers based on search and filters
  const filteredPerformers = sortedPerformers.filter((performer) => {
    const matchesSearch = performer.name
      .toLowerCase()
      .includes(searchTerm.toLowerCase());

    const matchesPriority =
      priorityFilter === "all" ||
      (priorityFilter === "priority" && performer.priority) ||
      (priorityFilter === "regular" && !performer.priority);

    return matchesSearch && matchesPriority;
  });

  return (
    <div className="bg-white/30 rounded-2xl shadow-lg border border-white/30 overflow-hidden relative">
      
      <div className="px-6 py-4 border-b border-white/20 relative z-10">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-slate-900/90 flex items-center drop-shadow-sm">
              <Users className="h-5 w-5 mr-2 text-green-600 drop-shadow-sm" />
              Attended
              <span className="ml-2 text-sm font-normal text-slate-600/80 drop-shadow-sm">
                ({filteredPerformers.length})
              </span>
            </h2>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="px-6 py-4 bg-white/10 border-b border-white/20 relative z-10">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative max-w-xs">
              <Input
                placeholder="Search attended participants..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pr-8"
              />
              {searchTerm && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="absolute right-1 top-1/2 -translate-y-1/2 h-6 w-6 p-0 hover:bg-transparent"
                  onClick={() => setSearchTerm("")}
                >
                  <X className="h-3 w-3 text-slate-400 hover:text-slate-600" />
                </Button>
              )}
            </div>
          </div>

          <div className="flex gap-2">
            <Select value={priorityFilter} onValueChange={(value: "all" | "priority" | "regular") => setPriorityFilter(value)}>
              <SelectTrigger className="w-[140px]">
                <SelectValue placeholder="Filter by priority" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="priority">Priority</SelectItem>
                <SelectItem value="regular">Regular</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Mobile Table */}
      <div className="block lg:hidden">
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={disabled ? undefined : handleDragEnd}
        >
          <SortableContext
            items={filteredPerformers.map(p => p.id)}
            strategy={verticalListSortingStrategy}
          >
            {filteredPerformers.map((performer) => (
              <AttendedMobileCard
                key={performer.id}
                performer={performer}
                onMarkNotAttended={handleMarkNotAttended}
                onRemove={handleRemovePerformer}
                isLoading={updatePerformerMutation.isPending}
                disabled={disabled}
                jamNight={jamNight}
              />
            ))}
          </SortableContext>
        </DndContext>
      </div>

      {/* Desktop Table */}
      <div className="hidden lg:block overflow-x-auto relative z-10">
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={disabled ? undefined : handleDragEnd}
        >
          <table className="min-w-full divide-y divide-slate-200/30">
            <thead className="bg-slate-50/40">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500/80 uppercase tracking-wider drop-shadow-sm">
                  Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500/80 uppercase tracking-wider drop-shadow-sm">
                  Priority
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500/80 uppercase tracking-wider drop-shadow-sm">
                  Attended
                </th>
                {jamNight?.raffleEnabled && (
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500/80 uppercase tracking-wider drop-shadow-sm">
                    Raffle
                  </th>
                )}
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500/80 uppercase tracking-wider drop-shadow-sm">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white/30 divide-y divide-slate-200/30">
              <SortableContext
                items={filteredPerformers.map(p => p.id)}
                strategy={verticalListSortingStrategy}
              >
                {filteredPerformers.map((performer) => (
                  <AttendedRow
                    key={performer.id}
                    performer={performer}
                    onMarkNotAttended={handleMarkNotAttended}
                    onRemove={handleRemovePerformer}
                    isLoading={updatePerformerMutation.isPending}
                    disabled={disabled}
                    jamNight={jamNight}
                  />
                ))}
              </SortableContext>
            </tbody>
          </table>
        </DndContext>
      </div>

      {filteredPerformers.length === 0 && performers.length > 0 && (
        <div className="text-center py-8 text-slate-500">
          <Users className="h-12 w-12 mx-auto mb-4 text-slate-300" />
          <p>No attended participants found matching your criteria.</p>
          <Button
            variant="outline"
            onClick={() => {
              setSearchTerm("");
              setPriorityFilter("all");
            }}
            className="mt-2"
          >
            Clear Filters
          </Button>
        </div>
      )}

      {performers.length === 0 && (
        <div className="text-center py-8 text-slate-500">
          <Users className="h-12 w-12 mx-auto mb-4 text-slate-300" />
          <p>No participants have attended yet.</p>
        </div>
      )}
    </div>
  );
}